"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { useBookingCart } from "~/hooks/use-booking-cart";
import { useOnboardingStatus } from "~/hooks/use-onboarding-status";
import { buildBookingReturnUrl } from "~/lib/booking-cart";

export function useBookingResume() {
  const pathname = usePathname();
  const { cartItem, returnUrl, clearBookingCart, isLoading } = useBookingCart();
  const { hasTherapyRelationship, isLoading: onboardingLoading } =
    useOnboardingStatus();
  const [now, setNow] = useState<Date | null>(null);

  useEffect(() => {
    const updateNow = () => setNow(new Date());
    updateNow();
    if (!cartItem) return;
    const boundaries = [
      new Date(cartItem.selectedDateTime).getTime() -
        (cartItem.minimumBookingLeadTime ?? 0) * 3600000,
      new Date(cartItem.expiresAt).getTime(),
    ];
    const timers = boundaries
      .map((boundary) => boundary - Date.now())
      .filter((delay) => delay > 0 && delay <= 2147483647)
      .map((delay) => window.setTimeout(updateNow, delay));
    window.addEventListener("focus", updateNow);
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      window.removeEventListener("focus", updateNow);
    };
  }, [cartItem]);

  const shouldHide =
    pathname.startsWith("/admin") ||
    pathname.startsWith("/app") ||
    pathname.startsWith("/verify-request") ||
    (pathname.includes("/terapeuti/") && pathname.includes("/book"));
  const isVisible =
    !shouldHide &&
    !isLoading &&
    !onboardingLoading &&
    !!now &&
    !!cartItem &&
    new Date(cartItem.expiresAt).getTime() > now.getTime() &&
    !hasTherapyRelationship;

  const resumeUrl =
    isVisible && cartItem && now && returnUrl
      ? buildBookingReturnUrl(cartItem, undefined, now.getTime())
      : null;
  return {
    cartItem: resumeUrl ? cartItem : null,
    resumeUrl,
    now,
    clearBookingCart,
    needsNewTime:
      !!resumeUrl &&
      new URL(resumeUrl, "http://localhost").searchParams.get("step") === "0",
  };
}
